const BASE_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

let accessToken: string | null = null;
let refreshPath = '/v1/auth/refresh';

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function setRefreshPath(path: string): void {
  refreshPath = path;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type SuccessEnvelope<T> = { data: T };
type ErrorEnvelope = { error: { code: string; message: string; details?: unknown } };

async function parseError(res: Response): Promise<ApiError> {
  try {
    const body = (await res.json()) as ErrorEnvelope;
    return new ApiError(res.status, body.error.code, body.error.message, body.error.details);
  } catch {
    return new ApiError(res.status, 'internal_error', res.statusText);
  }
}

async function doFetch<T>(url: string, init: RequestInit): Promise<T> {
  const res = await fetch(url, init);
  if (res.ok) {
    const body = (await res.json()) as SuccessEnvelope<T>;
    return body.data;
  }
  throw await parseError(res);
}

function buildInit(init: RequestInit): RequestInit {
  const headers = new Headers(init.headers);

  if (accessToken) {
    headers.set('Authorization', `Bearer ${accessToken}`);
  }

  if (init.body != null && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  return { ...init, headers, credentials: 'include' };
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const url = `${BASE_URL}${path}`;
  const builtInit = buildInit(init);

  const res = await fetch(url, builtInit);

  if (res.ok) {
    if (res.status === 204) return undefined as T;
    const body = (await res.json()) as SuccessEnvelope<T>;
    return body.data;
  }

  const err = await parseError(res);

  const isRefreshPath = path === refreshPath;

  if (res.status === 401 && err.code === 'unauthenticated' && !isRefreshPath) {
    // Attempt one token refresh; cookie sent automatically.
    // refreshPath is set to the admin endpoint when an admin session is active.
    let refreshed: { accessToken: string };
    try {
      refreshed = await doFetch<{ accessToken: string }>(
        `${BASE_URL}${refreshPath}`,
        { method: 'POST', credentials: 'include' },
      );
    } catch (refreshErr) {
      setAccessToken(null);
      throw refreshErr;
    }

    setAccessToken(refreshed.accessToken);
    return doFetch<T>(url, buildInit(init));
  }

  throw err;
}

export const api = {
  get<T>(path: string, init?: Omit<RequestInit, 'method' | 'body'>): Promise<T> {
    return apiFetch<T>(path, { ...init, method: 'GET' });
  },

  post<T>(path: string, body?: unknown, init?: Omit<RequestInit, 'method' | 'body'>): Promise<T> {
    const bodyInit: Pick<RequestInit, 'body'> = body !== undefined ? { body: JSON.stringify(body) } : {};
    return apiFetch<T>(path, { ...init, ...bodyInit, method: 'POST' });
  },

  patch<T>(path: string, body?: unknown, init?: Omit<RequestInit, 'method' | 'body'>): Promise<T> {
    const bodyInit: Pick<RequestInit, 'body'> = body !== undefined ? { body: JSON.stringify(body) } : {};
    return apiFetch<T>(path, { ...init, ...bodyInit, method: 'PATCH' });
  },

  put<T>(path: string, body?: unknown, init?: Omit<RequestInit, 'method' | 'body'>): Promise<T> {
    const bodyInit: Pick<RequestInit, 'body'> = body !== undefined ? { body: JSON.stringify(body) } : {};
    return apiFetch<T>(path, { ...init, ...bodyInit, method: 'PUT' });
  },

  delete<T>(path: string, init?: Omit<RequestInit, 'method' | 'body'>): Promise<T> {
    return apiFetch<T>(path, { ...init, method: 'DELETE' });
  },
};
